import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Button, Card, Input, Modal, Select } from '@copilot/ui';
import Badge from '../common/Badge';
import { Field } from '../common/Field';
import Loader from '../common/Loader';
import * as adminApi from '../../services/adminApi';

// Platform API credit monitoring (backend services/api_credits/). Every
// number shown comes from the backend: nothing here invents or defaults
// a balance. An unknown balance is shown as "Not Available", never 0.
// Nothing here can buy credit - "Record Top-up" only records credit an
// admin already bought on the provider's own site.

const STATUS_LABELS = {
  CONFIRMED_SNAPSHOT: 'Confirmed',
  ESTIMATED: 'Estimated',
  STALE: 'Stale',
  UNKNOWN: 'Unknown',
  EXHAUSTED_CONFIRMED: 'Credits Exhausted',
};

const ALERT_TYPE_LABELS = {
  low_credit: 'Low credit',
  credit_exhausted: 'Credits exhausted',
  billing_limit: 'Billing limit reached',
};

const ADJUSTMENT_TYPES = [
  { value: 'top_up', label: 'Top-up already purchased' },
  { value: 'credit_adjustment', label: 'Credit adjustment (e.g. promotional credit)' },
  { value: 'debit_adjustment', label: 'Debit adjustment (e.g. tax, external usage)' },
];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const AMOUNT_PATTERN = /^\d+(\.\d{1,6})?$/;

export function formatCreditAmount(symbol, value) {
  if (value === null || value === undefined || value === '') return 'Not Available';
  const number = Number(value);
  if (!Number.isFinite(number)) return 'Not Available';
  const formatted = Math.abs(number).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${number < 0 ? '-' : ''}${symbol}${formatted}`;
}

export function formatIst(isoString) {
  if (!isoString) return 'Not available';
  const formatted = new Date(isoString).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${formatted} IST`;
}

function newIdempotencyKey() {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function cardStatus(provider) {
  const { balance, last_confirmed: lastConfirmed } = provider;
  if (balance.status === 'EXHAUSTED_CONFIRMED') {
    return { label: 'Credits Exhausted', variant: 'disconnected' };
  }
  if (!lastConfirmed) return { label: 'Setup Required', variant: 'paused' };
  if (balance.is_low) return { label: 'Low Credit', variant: 'disconnected' };
  if (balance.status === 'STALE' || balance.status === 'UNKNOWN') {
    return { label: STATUS_LABELS[balance.status], variant: 'paused' };
  }
  return { label: 'OK', variant: 'listening' };
}

export function parseRecipients(text) {
  const addresses = text
    .split(/[\s,;]+/)
    .map((part) => part.trim())
    .filter(Boolean);
  const invalid = addresses.filter((address) => !EMAIL_PATTERN.test(address));
  return { addresses, invalid };
}

// ------------------------------------------------------------- modals ----

function SnapshotModal({ provider, policy, onClose, onSaved }) {
  const keyRef = useRef(newIdempotencyKey());
  const [balanceValue, setBalanceValue] = useState('');
  const [readAt, setReadAt] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const submit = async () => {
    const trimmed = balanceValue.trim();
    if (!AMOUNT_PATTERN.test(trimmed)) {
      setError('Enter the balance as a number, e.g. 500.00.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await adminApi.recordApiCreditSnapshot(provider.provider, {
        balance: trimmed,
        currency: provider.currency,
        snapshot_at: readAt ? new Date(readAt).toISOString() : undefined,
        notes: notes.trim() || undefined,
        idempotency_key: keyRef.current,
      });
      onSaved('Confirmed balance saved.');
    } catch (err) {
      setError(err.message || 'Unable to save the balance.');
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={`Update Confirmed Balance — ${provider.label}`}
      subtitle="Copy the balance shown on the provider's billing dashboard."
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Balance'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={`Balance (${provider.currency})`} htmlFor="credit-snapshot-balance">
          <Input
            id="credit-snapshot-balance"
            inputMode="decimal"
            value={balanceValue}
            onChange={(event) => setBalanceValue(event.target.value)}
            placeholder="500.00"
          />
        </Field>
        <Field label="Read on the dashboard at (optional, defaults to now)" htmlFor="credit-snapshot-at">
          <Input
            id="credit-snapshot-at"
            type="datetime-local"
            value={readAt}
            onChange={(event) => setReadAt(event.target.value)}
          />
        </Field>
        <Field label="Notes (optional)" htmlFor="credit-snapshot-notes">
          <Input
            id="credit-snapshot-notes"
            value={notes}
            maxLength={500}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
        <a href={provider.billing_url} target="_blank" rel="noopener noreferrer">
          Open {provider.label} billing dashboard
        </a>
        {policy.confirmedOnly && (
          <p className="admin-table-subtext">
            Recording a balance does not fetch it from {provider.label}. A low-credit alert uses it only
            if it was read within the last {policy.windowMinutes} minutes.
          </p>
        )}
        {error && <div className="form-alert">{error}</div>}
      </div>
    </Modal>
  );
}

function AdjustmentModal({ provider, onClose, onSaved }) {
  const keyRef = useRef(newIdempotencyKey());
  const [entryType, setEntryType] = useState('top_up');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const submit = async () => {
    const trimmed = amount.trim();
    if (!AMOUNT_PATTERN.test(trimmed) || Number(trimmed) <= 0) {
      setError('Enter a positive amount, e.g. 250.00.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await adminApi.recordApiCreditAdjustment(provider.provider, {
        entry_type: entryType,
        amount: trimmed,
        currency: provider.currency,
        notes: notes.trim() || undefined,
        idempotency_key: keyRef.current,
      });
      onSaved('Adjustment recorded.');
    } catch (err) {
      setError(err.message || 'Unable to record the adjustment.');
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={`Record Top-up or Adjustment — ${provider.label}`}
      subtitle="This only records credit already bought on the provider's site. KrishvaQ never makes a payment."
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Record'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label="Type" htmlFor="credit-adjustment-type">
          <Select
            id="credit-adjustment-type"
            value={entryType}
            onChange={(event) => setEntryType(event.target.value)}
          >
            {ADJUSTMENT_TYPES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={`Amount (${provider.currency})`} htmlFor="credit-adjustment-amount">
          <Input
            id="credit-adjustment-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </Field>
        <Field label="Notes (optional)" htmlFor="credit-adjustment-notes">
          <Input
            id="credit-adjustment-notes"
            value={notes}
            maxLength={500}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
        {error && <div className="form-alert">{error}</div>}
      </div>
    </Modal>
  );
}

function SettingsModal({ provider, policy, onClose, onSaved }) {
  const [enabled, setEnabled] = useState(provider.enabled);
  const [threshold, setThreshold] = useState(String(Number(provider.low_balance_threshold)));
  const [interval, setIntervalMinutes] = useState(String(provider.monitor_interval_minutes));
  const [recipients, setRecipients] = useState((provider.recipient_emails || []).join(', '));
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const submit = async () => {
    const { addresses, invalid } = parseRecipients(recipients);
    if (invalid.length) {
      setError(`Not a valid email address: ${invalid.join(', ')}`);
      return;
    }
    if (!AMOUNT_PATTERN.test(threshold.trim())) {
      setError('Enter the threshold as a number, e.g. 100.00.');
      return;
    }
    const minutes = Number(interval);
    if (!Number.isInteger(minutes) || minutes < 5 || minutes > 1440) {
      setError('The check interval must be between 5 and 1440 minutes.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      await adminApi.updateApiCreditSettings({
        provider: provider.provider,
        enabled,
        low_balance_threshold: threshold.trim(),
        monitor_interval_minutes: minutes,
        recipient_emails: addresses,
      });
      onSaved('Settings saved.');
    } catch (err) {
      setError(err.message || 'Unable to save settings.');
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={`Alert Settings — ${provider.label}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Settings'}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <label className="admin-plan-toggle" htmlFor="credit-settings-enabled">
          <input
            id="credit-settings-enabled"
            type="checkbox"
            checked={enabled}
            onChange={(event) => setEnabled(event.target.checked)}
          />
          Monitor {provider.label}
        </label>
        <Field label={`Low-credit threshold (${provider.currency})`} htmlFor="credit-settings-threshold">
          <Input
            id="credit-settings-threshold"
            inputMode="decimal"
            value={threshold}
            disabled={policy.confirmedOnly}
            onChange={(event) => setThreshold(event.target.value)}
          />
        </Field>
        {policy.confirmedOnly && (
          <div className="admin-table-subtext">
            Fixed in confirmed-only mode: a confirmed balance at or below{' '}
            {formatCreditAmount(provider.symbol, provider.low_balance_threshold)} is low.
          </div>
        )}
        <Field label="Check interval (minutes)" htmlFor="credit-settings-interval">
          <Input
            id="credit-settings-interval"
            type="number"
            min={5}
            max={1440}
            value={interval}
            onChange={(event) => setIntervalMinutes(event.target.value)}
          />
        </Field>
        <Field
          label="Alert recipients (administrators, comma separated)"
          htmlFor="credit-settings-recipients"
        >
          <Input
            id="credit-settings-recipients"
            value={recipients}
            onChange={(event) => setRecipients(event.target.value)}
            placeholder="ops@example.com, owner@example.com"
          />
        </Field>
        {provider.uses_default_recipients && provider.effective_recipients.length > 0 && (
          <div className="admin-table-subtext">
            Empty list: alerts go to the server default ({provider.effective_recipients.join(', ')}).
          </div>
        )}
        {error && <div className="form-alert">{error}</div>}
      </div>
    </Modal>
  );
}

function AlertHistoryModal({ provider, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    adminApi
      .getApiCreditAlerts({ provider: provider.provider, page_size: 50 })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || 'Unable to load alert history.');
      });
    return () => {
      cancelled = true;
    };
  }, [provider.provider, reload]);

  const resolve = async (incidentId) => {
    try {
      await adminApi.resolveApiCreditAlert(incidentId);
      setReload((value) => value + 1);
      onChanged();
    } catch (err) {
      setError(err.message || 'Unable to resolve the alert.');
    }
  };

  const items = data ? data.items : [];

  return (
    <Modal
      title={`Alert History — ${provider.label}`}
      onClose={onClose}
      size="lg"
      actions={<Button onClick={onClose}>Close</Button>}
    >
      {!data && !error && <Loader label="Loading alert history..." />}
      {error && <div className="form-alert">{error}</div>}
      {data && items.length === 0 && <p>No alerts have been raised for {provider.label}.</p>}
      {items.length > 0 && (
        <div className="admin-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Alert</th>
                <th>Opened</th>
                <th>Remaining</th>
                <th>Emails</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    {ALERT_TYPE_LABELS[item.alert_type] || item.alert_type}
                    {item.error_code && <div className="admin-table-subtext">{item.error_code}</div>}
                  </td>
                  <td>{formatIst(item.opened_at)}</td>
                  <td>
                    {item.remaining_estimate === null
                      ? '—'
                      : formatCreditAmount(provider.symbol, item.remaining_estimate)}
                  </td>
                  <td>
                    {item.deliveries.length === 0 && 'None queued'}
                    {item.deliveries.map((delivery) => (
                      <div key={delivery.id} className="admin-table-subtext">
                        {delivery.recipient}: {delivery.status}
                        {delivery.sent_at && ` (${formatIst(delivery.sent_at)})`}
                        {delivery.error_code && ` — ${delivery.error_code}`}
                      </div>
                    ))}
                  </td>
                  <td>
                    {item.resolved_at ? (
                      <>
                        Resolved ({item.resolution})
                        <div className="admin-table-subtext">{formatIst(item.resolved_at)}</div>
                      </>
                    ) : (
                      <Button variant="secondary" onClick={() => resolve(item.id)}>
                        Mark Resolved
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}

// --------------------------------------------------------------- card ----

// Confirmed-only mode: only what an administrator read on the provider's
// dashboard, with its time. No estimate and no "remaining" figure.
function ConfirmedBalanceDetails({ provider, policy }) {
  const lastConfirmed = provider.last_confirmed;
  const lowIncidentOpen = provider.open_incidents.some((incident) => incident.alert_type === 'low_credit');

  return (
    <>
      <dl className="admin-status-meta">
        <div className="admin-status-meta-row">
          <dt>Last administrator-confirmed balance</dt>
          <dd>{formatCreditAmount(provider.symbol, lastConfirmed.balance)}</dd>
        </div>
        <div className="admin-status-meta-row">
          <dt>Balance observed at</dt>
          <dd>{formatIst(lastConfirmed.snapshot_at)}</dd>
        </div>
        <div className="admin-status-meta-row">
          <dt>Low-credit threshold</dt>
          <dd>{formatCreditAmount(provider.symbol, provider.low_balance_threshold)}</dd>
        </div>
        <div className="admin-status-meta-row">
          <dt>Low-credit incident</dt>
          <dd>{lowIncidentOpen ? 'Open' : 'None open'}</dd>
        </div>
        <div className="admin-status-meta-row">
          <dt>Exact automatic balance retrieval</dt>
          <dd>{provider.exact_balance_retrieval_supported ? 'Supported' : 'Not supported'}</dd>
        </div>
      </dl>
      <p className="admin-table-subtext">
        This is the balance an administrator confirmed at the time shown. It is not a live reading from{' '}
        {provider.label}, so the current balance may be different.
      </p>
      {provider.last_confirmed_within_alert_window === false && (
        <p className="admin-table-subtext">
          Read more than {policy.windowMinutes} minutes ago, so it does not open or close a low-credit alert.
          Update the confirmed balance to check again.
        </p>
      )}
    </>
  );
}

function ProviderCreditCard({ provider, busy, onAction, policy }) {
  const { balance } = provider;
  const status = cardStatus(provider);
  const lastConfirmed = provider.last_confirmed;
  const needsSetup = !lastConfirmed;

  return (
    <div className="api-credit-card-cell" data-testid={`api-credit-card-${provider.provider}`}>
      <Card className="admin-status-card api-credit-card">
        <div className="admin-status-card-header">
          <span className="stat-card-label">{provider.label}</span>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>

        {needsSetup ? (
          <dl className="admin-status-meta">
            <div className="admin-status-meta-row">
              <dt>Remaining Balance</dt>
              <dd>Not Available</dd>
            </div>
            <div className="admin-status-meta-row">
              <dt>Status</dt>
              <dd>Setup Required</dd>
            </div>
            <div className="admin-status-meta-row">
              <dt>Threshold</dt>
              <dd>{formatCreditAmount(provider.symbol, provider.low_balance_threshold)}</dd>
            </div>
          </dl>
        ) : policy.confirmedOnly ? (
          <ConfirmedBalanceDetails provider={provider} policy={policy} />
        ) : (
          <dl className="admin-status-meta">
            <div className="admin-status-meta-row">
              <dt>{balance.remaining_label}</dt>
              <dd>
                {balance.status === 'EXHAUSTED_CONFIRMED'
                  ? 'Confirmed by provider error'
                  : formatCreditAmount(provider.symbol, balance.remaining)}
              </dd>
            </div>
            {balance.remaining === null && balance.last_estimate !== null && (
              <div className="admin-status-meta-row">
                <dt>Last estimate (not current)</dt>
                <dd>{formatCreditAmount(provider.symbol, balance.last_estimate)}</dd>
              </div>
            )}
            <div className="admin-status-meta-row">
              <dt>Threshold</dt>
              <dd>{formatCreditAmount(provider.symbol, provider.low_balance_threshold)}</dd>
            </div>
            <div className="admin-status-meta-row">
              <dt>Accuracy</dt>
              <dd>{STATUS_LABELS[balance.status] || balance.status}</dd>
            </div>
            <div className="admin-status-meta-row">
              <dt>Last confirmed balance</dt>
              <dd>{formatCreditAmount(provider.symbol, lastConfirmed.balance)}</dd>
            </div>
            <div className="admin-status-meta-row">
              <dt>Confirmed at</dt>
              <dd>{formatIst(lastConfirmed.snapshot_at)}</dd>
            </div>
            <div className="admin-status-meta-row">
              <dt>{balance.latest_reconciliation_label}</dt>
              <dd>{formatIst(balance.latest_reconciliation_at)}</dd>
            </div>
          </dl>
        )}

        {needsSetup && (
          <p className="admin-table-subtext">
            Add the current balance from your provider&apos;s billing dashboard.
          </p>
        )}
        {!needsSetup && balance.reason_message && (
          <p className="admin-table-subtext">{balance.reason_message}</p>
        )}
        {balance.status === 'ESTIMATED' && (
          <p className="admin-table-subtext">{provider.estimate_note}</p>
        )}
        {provider.warnings.length > 0 && (
          <ul className="api-credit-warnings">
            {provider.warnings.map((warning) => (
              <li key={warning}>{warning}</li>
            ))}
          </ul>
        )}

        <div className="api-credit-actions">
          <Button onClick={() => onAction('snapshot', provider)} disabled={busy}>
            Update Confirmed Balance
          </Button>
          <Button variant="secondary" onClick={() => onAction('adjustment', provider)} disabled={busy}>
            Record Top-up
          </Button>
          <Button variant="secondary" onClick={() => onAction('history', provider)} disabled={busy}>
            View Alert History
          </Button>
          <Button variant="secondary" onClick={() => onAction('settings', provider)} disabled={busy}>
            Settings
          </Button>
          <Button variant="ghost" onClick={() => onAction('check', provider)} disabled={busy}>
            Check Now
          </Button>
          <Button variant="ghost" onClick={() => onAction('test-email', provider)} disabled={busy}>
            Send Test Email
          </Button>
          <a href={provider.billing_url} target="_blank" rel="noopener noreferrer">
            Billing dashboard
          </a>
        </div>
      </Card>
    </div>
  );
}

// -------------------------------------------------------------- panel ----

function ApiCreditMonitoringPanel() {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null); // { kind, provider }

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setIsLoading(true);
    setError('');
    try {
      const result = await adminApi.getApiCreditOverview();
      setData(result || null);
    } catch (err) {
      setError(err.message || 'Unable to load API credit monitoring.');
    } finally {
      if (!silent) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const closeModal = () => setModal(null);

  const policy = {
    confirmedOnly: Boolean(data && data.low_alert_mode === 'confirmed_only'),
    windowMinutes: (data && data.confirmed_alert_max_age_minutes) || 60,
  };

  const onSaved = (message) => {
    setModal(null);
    setNotice(message);
    setActionError('');
    load({ silent: true });
  };

  const runAction = async (kind, provider) => {
    if (['snapshot', 'adjustment', 'history', 'settings'].includes(kind)) {
      setModal({ kind, provider });
      return;
    }
    setBusy(true);
    setNotice('');
    setActionError('');
    try {
      if (kind === 'check') {
        const result = await adminApi.checkApiCreditsNow(provider.provider);
        setNotice(
          result.ran
            ? `Checked ${provider.label}. ${result.note}`
            : 'Another check is already running; try again shortly.'
        );
        await load({ silent: true });
      } else if (kind === 'test-email') {
        const result = await adminApi.sendApiCreditTestEmail(provider.provider);
        const sent = result.deliveries.filter((d) => d.status === 'sent').length;
        const failed = result.deliveries.length - sent;
        if (failed) {
          setActionError(
            `Test email: ${sent} sent, ${failed} failed (${result.deliveries
              .filter((d) => d.status !== 'sent')
              .map((d) => `${d.recipient}: ${d.error_code || d.status}`)
              .join(', ')}).`
          );
        } else {
          setNotice(`Test email sent to ${sent} recipient${sent === 1 ? '' : 's'}.`);
        }
      }
    } catch (err) {
      setActionError(err.message || 'The action failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="api-credit-panel" aria-labelledby="api-credit-heading">
      <div className="admin-card-header-row admin-section-heading">
        <div className="card-title" id="api-credit-heading">
          API Credit Monitoring
        </div>
      </div>

      {isLoading && <Loader label="Loading API credit status..." />}

      {!isLoading && error && (
        <div className="form-alert" role="alert">
          {error}{' '}
          <Button variant="secondary" onClick={() => load()}>
            Retry
          </Button>
        </div>
      )}

      {!isLoading && !error && data && (
        <>
          <p className="admin-table-subtext">
            Alerts for the platform&apos;s own OpenAI and Sarvam prepaid credit. Auto recharge:{' '}
            {data.auto_recharge}.
          </p>
          {policy.confirmedOnly && (
            <p className="admin-table-subtext">
              Low-credit alerts use only balances an administrator confirmed on the provider&apos;s billing
              dashboard, compared with a fixed threshold. Estimates never trigger an alert.
            </p>
          )}
          <p className="admin-table-subtext">
            Check Now re-reads tracked spending. It cannot fetch the exact remaining prepaid credit with the
            existing provider integrations.
          </p>
          {data.config_warnings.length > 0 && (
            <ul className="api-credit-warnings" aria-label="Configuration warnings">
              {data.config_warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          )}
          {notice && <div className="admin-action-status">{notice}</div>}
          {actionError && (
            <div className="form-alert" role="alert">
              {actionError}
            </div>
          )}

          <div className="stat-cards admin-status-cards api-credit-cards">
            {data.providers.map((provider) => (
              <ProviderCreditCard
                key={provider.provider}
                provider={provider}
                busy={busy}
                onAction={runAction}
                policy={policy}
              />
            ))}
          </div>
        </>
      )}

      {modal && modal.kind === 'snapshot' && (
        <SnapshotModal provider={modal.provider} policy={policy} onClose={closeModal} onSaved={onSaved} />
      )}
      {modal && modal.kind === 'adjustment' && (
        <AdjustmentModal provider={modal.provider} onClose={closeModal} onSaved={onSaved} />
      )}
      {modal && modal.kind === 'settings' && (
        <SettingsModal provider={modal.provider} policy={policy} onClose={closeModal} onSaved={onSaved} />
      )}
      {modal && modal.kind === 'history' && (
        <AlertHistoryModal
          provider={modal.provider}
          onClose={closeModal}
          onChanged={() => load({ silent: true })}
        />
      )}
    </section>
  );
}

export default ApiCreditMonitoringPanel;
